import { styled } from "@linaria/react";
import React from "react";
import { useNavigate } from "react-router";
import { usePost } from "../../../../api/useCall";
import useToasts from "@ui/Toast/useToasts";
import t from "../../../../i18n";
import ModalForm from "@ui/Modal/Form";
import { useQuantities } from "../../../../api/useQuantities";
import type { ParsedCsvFilters } from "./parseCsvFilterValues";
import parseCsvFilterValues from "./parseCsvFilterValues";
import DropzoneV2 from "@ui/DropzoneV2";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import download from "downloadjs";
import { useMutateBody } from "../../../../api/useQuery";
import { toPath } from "../../../../router/helpers/path";

const DropzoneContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CSV_MIME_TYPE = ".csv";

interface P {
  onLoadFilterClose: () => void;
  closeLoadFiltersModal: () => void;
  projectId: string;
  isEdit: boolean;
}

const LoadFiltersModalForm = ({
  onLoadFilterClose,
  closeLoadFiltersModal,
  projectId,
  isEdit,
}: P) => {
  const [filename, setFilename] = React.useState<string>("");
  const { addToast } = useToasts();
  const navigate = useNavigate();
  const replaceActivationFilters = usePost("/projects/:projectId(uuid)/activationFilters");
  const mutateQuantities = useMutateBody("/projects/:projectId(uuid)/quantities", {
    routeParams: { projectId },
  });

  const [parsedFilters, setParsedFilters] = React.useState<ParsedCsvFilters>({
    invalidCsv: false,
    data: [],
  });
  const [invalidFileExtension, setInvalidFileExtension] = React.useState<boolean | undefined>();
  const isValid = !invalidFileExtension && !parsedFilters.invalidCsv;
  const quantities = useQuantities(projectId);
  const handleSubmit = async () => {
    if (!parsedFilters.invalidCsv) {
      await replaceActivationFilters({
        body: { fileName: filename, filters: parsedFilters.data },
        routeParams: { projectId },
      });

      mutateQuantities(({ collection, ...rest }) => ({
        ...rest,
        collection: collection.map(q => {
          const newFilters = parsedFilters.data.find(f => f.quantityId === q.id);
          return {
            ...q,
            maxActivation: newFilters?.max ?? null,
            minActivation: newFilters?.min ?? null,
            maxStdActivation: newFilters?.maxStd ?? null,
          };
        }),
      }));

      addToast(
        "success",
        t("project.filters.modal.import.toast.success", {
          count: parsedFilters.data.length,
        }),
      );
      onLoadFilterClose();

      if (!isEdit) {
        void navigate(toPath("/projects/:projectId/settings/filters", { projectId }));
      }
    }

    return true;
  };

  const handleDrop = React.useCallback(
    (files: File[]) => {
      setInvalidFileExtension(false);
      setParsedFilters({ invalidCsv: false, data: [], format: undefined });

      if (files.length > 1) {
        addToast("error", t("sources.patchModal.action.import.errorMultipleFiles"));
        return;
      }

      const uploadedFile = files[0];
      if (files.length === 0) {
        setInvalidFileExtension(true);
        setFilename("");
        return;
      }

      if (uploadedFile === undefined) {
        throw new Error("should not happen");
      }

      const reader = new FileReader();
      reader.onload = async () => {
        if (typeof reader.result !== "string") {
          throw new Error("Bad encoding");
        }

        const parsedFilters = await parseCsvFilterValues(uploadedFile, quantities);

        setFilename(uploadedFile.name);
        if (parsedFilters !== undefined) {
          setParsedFilters(parsedFilters);
        }
      };

      reader.readAsText(uploadedFile);
    },
    [addToast, quantities],
  );

  const downloadErrors = () => {
    const errors = parsedFilters.invalidCsv ? parsedFilters.errors : [];
    download(JSON.stringify(errors) as unknown as Blob, "error" + ".txt");
    addToast("info", t("setup.paragraphs.model.error.download"));
  };

  const getFormattedErrors = (errors: string[]) => (
    <>
      {errors.map((m, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={index}>
          {`(${index + 1}) ${m}`}
          {index < errors.length - 1 ? <br /> : null}
        </React.Fragment>
      ))}
    </>
  );

  return (
    <ModalForm
      onRequestClose={closeLoadFiltersModal}
      onSubmit={!parsedFilters.invalidCsv && parsedFilters.data.length > 0 ? handleSubmit : false}
      okButtonName={t("modalForm.button.name.import")}
    >
      <DropzoneContainer>
        <DropzoneV2
          accept={CSV_MIME_TYPE}
          onDrop={handleDrop}
          pathSampleFile="/metroscopia_example_filter.csv"
          nameFile={filename}
          validation={invalidFileExtension === undefined ? undefined : isValid}
          errorText={
            "errors" in parsedFilters
              ? getFormattedErrors(parsedFilters.errors.flatMap(m => m.errors))
              : undefined
          }
          version={parsedFilters.format}
          downloadErrors={downloadErrors}
        />
        {invalidFileExtension !== undefined && isValid ? (
          <>
            <AdditionnalMessage
              status="INFO"
              text={t("project.settings.analyses.add_filters.modal.info.description")}
            />
            {invalidFileExtension && (
              <AdditionnalMessage
                status="WARNING"
                text={t("project.settings.analyses.add_filters.invalid.file_type")}
              />
            )}
          </>
        ) : null}
      </DropzoneContainer>
    </ModalForm>
  );
};

export default LoadFiltersModalForm;
